<script>
import { computed, inject, ref, onMounted, onUnmounted } from 'vue';
import _head from 'lodash/head';
import { success } from "@/use/toast/success";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { error } from "@/use/toast/error";
import { dateFormatter } from '@/use/utilities/time/dateFormatter.js';
import { update as updateTestWorkQueuePhase } from '@/use/repositories/testWorkQueuePhase/update';
import { pending } from '@/use/repositories/testWorkQueuePhase/pending';
import { useStateIcon } from '@/composables/useStateIcon';
import { useTestWorkQueueTypesOptions } from '@/composables/useTestWorkQueueTypesOptions';
import Spinner from '@/components/general/Spinner.vue';
import StandardModal from '@/components/Modal/Standard.vue';
import Multiselect from "@vueform/multiselect";

export default {
  name: 'Pendings',
  emits: ['assigned'],
  components: {
    Spinner,
    StandardModal,
    Multiselect,
  },
  setup(_, { emit }) {

    const pendingPhases = ref([]);
    const i18n = useI18n();
    const store = useStore();
    const { getTestWorkQueueTypeIcon } = useStateIcon();
    const { listCategories } = inject("constants");
    const collapseStates = ref({});
    const isLoading = ref(false);
    const isRefreshing = ref(false);
    const selectedCategory = ref(null);
    const showModal = ref(false);
    const modalMessage = ref('');
    const modalId = 'assign-warning-modal';
    const assignParams = ref(null);
    const otherWorksList = ref([]);
    const selectedPhases = ref(new Set());
    const validOtherWorksList = computed(() =>
      otherWorksList.value.filter(
        work => work && work.test_work_queue && work.test_work_queue.prescription_test
      )
    );
    const allWorksList = computed(() => {
      if (!assignParams.value) return validOtherWorksList.value;
      return validOtherWorksList.value.filter(w => w.id !== assignParams.value.id);
    });

    const selectedTypes = ref([]);
    const appliedSelectedTypes = ref([]);
    let typeFilterTimeout = null;
    const { testWorkQueueTypesOptions } = useTestWorkQueueTypesOptions();

    const sortedAndLimitedPhases = computed(() => {
      let filteredPhases = [...pendingPhases.value];


      if (appliedSelectedTypes.value && appliedSelectedTypes.value.length) {
        filteredPhases = filteredPhases.filter(phase => {
          const workQueueTypes = phase.test_work_queue?.types || [];
          return appliedSelectedTypes.value.some(selectedType =>
            workQueueTypes.some(type => type.slug === selectedType)
          );
        });
      }

      return filteredPhases
        .sort((a, b) => {
          const dateA = a.test_work_queue.delivery_date;
          const dateB = b.test_work_queue.delivery_date;
          return (dateA ? new Date(dateA).getTime() : Infinity) - (dateB ? new Date(dateB).getTime() : Infinity);
        });
    });

    const getPrescriptionTitle = (phase) => {
      const customer = phase.test_work_queue.prescription_test?.prescription?.customer;
      const isZebra = phase.test_work_queue.prescription_test?.is_zebra || false;
      if (!customer) return 'N/A';
      return `${customer.first_name || ''} ${customer.last_name || ''}${isZebra ? ' z' : ''}`.trim();
    };

    const getBorderColorByFeedbackType = (phase) => {
      const feedbackType = _head(phase.feedbacks)?.feedback?.type?.slug;
      return {
        info: 'bg-color-info',
        warning: 'bg-color-warning',
      }[feedbackType] || '';
    };

    const getPendingData = (phase) => {
      const feedback = _head(phase.feedbacks);
      const date = feedback?.created_at ?? phase.test_work_queue.delivery_date;
      return date ? dateFormatter(date) : '-';
    };

    const notifyAssigned = (assignedPhaseOrPhases) => {
      emit('assigned', assignedPhaseOrPhases);
    };

    const getAssignErrorMessage = (err) => {
      return err.response?.data?.errors?.[0] || i18n.t('Failed to assign. Please try again.');
    };

    const getMultiAssignSuccessMessage = (successful, failed) => {
      const key = successful === 1
        ? 'Assignment was successful! {count} work assigned.'
        : 'Assignment was successful! {count} works assigned.';
      const baseMessage = i18n.t(key, { count: successful });

      if (failed === 0) {
        return baseMessage;
      }

      const failedKey = failed === 1
        ? '{count} work failed due to concurrency issues.'
        : '{count} works failed due to concurrency issues.';

      return `${baseMessage} ${i18n.t(failedKey, { count: failed })}`;
    };

    const getMultiAssignErrorMessage = (failed) => {
      const key = failed === 1
        ? '{count} work could not be assigned due to concurrency issues. Please refresh and try again.'
        : '{count} works could not be assigned due to concurrency issues. Please refresh and try again.';

      return i18n.t(key, { count: failed });
    };

    const handleAssignToMe = async (phase) => {
      if (isLoading.value) return;
      isLoading.value = true;
      try {
        const customerId = phase.test_work_queue.prescription_test?.prescription?.customer?.id;
        if (!customerId) {
          error(i18n.t('Customer information not available'));
          return;
        }
        const departmentSlug = store.state.mainRole.team.name;
        const params = {
          department_slug: departmentSlug,
          customer_id: customerId,
        };
        const { pendingTestWorkQueuePhases } = await fetchPendings(params, { loading: false });
        const otherWorks = (pendingTestWorkQueuePhases.data || []).filter(p => p.id !== phase.id);
        if (otherWorks.length > 0) {
          modalMessage.value = i18n.t('There are already other procedures for this patient in the department. Do you want to continue?');
          showModal.value = true;
          assignParams.value = phase;
          otherWorksList.value = otherWorks;
        } else {
          await doAssign(phase);
        }
      } catch (err) {
        error(i18n.t('Errore durante il controllo delle lavorazioni.'));
      } finally {
        isLoading.value = false;
      }
    };

    const doAssign = async (phase) => {
      isLoading.value = true;
      try {
        await updateTestWorkQueuePhase({
          source_ids: [phase.id],
          user_id: store.state.user.id,
          action: 'attach_user',
          check_concurrency: true,
          updated_at: phase.updated_at,
        });
        notifyAssigned(phase);
        success(i18n.t('Assignment was successful!'));
      } catch (err) {
        const errorMessage = getAssignErrorMessage(err);
        error(errorMessage);
      } finally {
        isLoading.value = false;
        showModal.value = false;
        assignParams.value = null;
        otherWorksList.value = [];
      }
    };

    const doAssignSingle = async (phase) => {
      isLoading.value = true;
      try {
        await updateTestWorkQueuePhase({
          source_ids: [phase.id],
          user_id: store.state.user.id,
          action: 'attach_user',
          check_concurrency: true,
          updated_at: phase.updated_at,
        });
        notifyAssigned(phase);
        success(i18n.t('Assignment was successful!'));
        await getPendingTestWorkQueuePhases();
      } catch (err) {
        const errorMessage = getAssignErrorMessage(err);
        error(errorMessage);
        await getPendingTestWorkQueuePhases();
      } finally {
        isLoading.value = false;
        showModal.value = false;
        assignParams.value = null;
        otherWorksList.value = [];
      }
    };

    const onModalClose = () => {
      showModal.value = false;
      assignParams.value = null;
      otherWorksList.value = [];
    };

    const onModalAssignSingle = async () => {
      if (assignParams.value) {
        if (assignParams.value.phaseIds && assignParams.value.phases) {
          await doMultiAssign(assignParams.value.phases);
        } else {
          await doAssignSingle(assignParams.value);
        }
      }
    };

    const onTypeFilterChange = () => {
      isLoading.value = true;
      if (typeFilterTimeout) clearTimeout(typeFilterTimeout);
      typeFilterTimeout = setTimeout(() => {
        appliedSelectedTypes.value = [...selectedTypes.value];
        isLoading.value = false;
      }, 1000);
    };

    const toggleCollapse = (id) => {
      collapseStates.value[id] = !collapseStates.value[id];
    };


    const refreshPendingPhases = async () => {
      isRefreshing.value = true;
      await getPendingTestWorkQueuePhases();
      isRefreshing.value = false;
    };

    const setCategory = (category) => {
      selectedCategory.value = category;
      getPendingTestWorkQueuePhases();
    };

    const togglePhaseSelection = (phaseId) => {
      if (selectedPhases.value.has(phaseId)) {
        selectedPhases.value.delete(phaseId);
      } else {
        selectedPhases.value.add(phaseId);
      }
    };

    const isPhaseSelected = (phaseId) => {
      return selectedPhases.value.has(phaseId);
    };


    const handleMultiAssign = async () => {
      if (isLoading.value || selectedPhases.value.size === 0) return;

      isLoading.value = true;
      try {
        const phaseIds = Array.from(selectedPhases.value);
        const phases = sortedAndLimitedPhases.value.filter(phase => phaseIds.includes(phase.id));

        const phasesByCustomer = {};
        phases.forEach(phase => {
          const customerId = phase.test_work_queue.prescription_test?.prescription?.customer?.id;
          if (customerId) {
            if (!phasesByCustomer[customerId]) {
              phasesByCustomer[customerId] = {
                customer: phase.test_work_queue.prescription_test.prescription.customer,
                phases: []
              };
            }
            phasesByCustomer[customerId].phases.push(phase);
          }
        });

        const departmentSlug = store.state.mainRole.team.name;
        const allConflicts = [];

        for (const [customerId, customerData] of Object.entries(phasesByCustomer)) {
          const params = {
            department_slug: departmentSlug,
            customer_id: customerId,
          };
          const { pendingTestWorkQueuePhases } = await fetchPendings(params, { loading: false });
          const otherWorks = (pendingTestWorkQueuePhases.data || [])
            .filter(p => !phaseIds.includes(p.id));

          if (otherWorks.length > 0) {
            allConflicts.push({
              customer: customerData.customer,
              phases: customerData.phases,
              otherWorks: otherWorks
            });
          }
        }
        if (allConflicts.length > 0) {
          modalMessage.value = i18n.t('There are conflicts with multiple patients. Review the details below:');
          showModal.value = true;
          assignParams.value = { phaseIds, phases, conflicts: allConflicts };
          otherWorksList.value = allConflicts.flatMap(c => c.otherWorks);
          return;
        }

        await doMultiAssign(phases);
      } catch (err) {
        error(i18n.t('Errore durante il controllo delle lavorazioni.'));
      } finally {
        isLoading.value = false;
      }
    };

    const doMultiAssign = async (phases) => {
      isLoading.value = true;
      try {
        const results = [];
        for (const phase of phases) {
          try {
            await updateTestWorkQueuePhase({
              source_ids: [phase.id],
              user_id: store.state.user.id,
              action: 'attach_user',
              check_concurrency: true,
              updated_at: phase.updated_at,
            });
            results.push({ success: true, phase });
          } catch (err) {
            results.push({ success: false, phase, error: err });
          }
        }

        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;
        const assignedPhases = results.filter(r => r.success).map(r => r.phase);

        if (successful > 0) {
          notifyAssigned(assignedPhases);
          success(getMultiAssignSuccessMessage(successful, failed));
        }

        if (failed > 0) {
          error(getMultiAssignErrorMessage(failed));
        }

        selectedPhases.value.clear();
        await getPendingTestWorkQueuePhases();
      } catch (err) {
        const errorMessage = getAssignErrorMessage(err);
        error(errorMessage);
        await getPendingTestWorkQueuePhases();
      } finally {
        isLoading.value = false;
        showModal.value = false;
        assignParams.value = null;
        otherWorksList.value = [];
      }
    };

    const getMaterial = (phase) => {
      return phase?.test_work_queue?.prescription_test?.prescription?.material ?? '-';
    };

    const getElementsCount = (phase) => {
      return phase?.test_work_queue?.prescription_test?.prescription?.prescription_rows_count ?? '-'
    };

    const fetchPendings = async (params = {}, options = {}) => {
      const effectiveOptions = { ...options, abort: options.abort === true };
      try {
        return await pending(params, false, effectiveOptions);
      } catch (error) {
        console.error(error);
      }
    };

    let lastPendingsFetchId = 0;
    const getPendingTestWorkQueuePhases = async () => {
      isLoading.value = true;
      try {
        const requestId = ++lastPendingsFetchId;
        const params = {
          department_slug: store.state.mainRole.team.name,
          page: 1,
          per_page: 20,
        };
        if (selectedCategory.value) {
          params.list_category_slug = selectedCategory.value;
        }
        const { pendingTestWorkQueuePhases } = await fetchPendings(params, { abort: true });
        if (requestId === lastPendingsFetchId) {
          pendingPhases.value = pendingTestWorkQueuePhases.data;
        }
      } catch (err) {
        console.error(err);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      getPendingTestWorkQueuePhases();
    });

    onUnmounted(() => {
      if (typeFilterTimeout) clearTimeout(typeFilterTimeout);
    });

    return {
      pendingPhases,
      sortedAndLimitedPhases,
      getPrescriptionTitle,
      getBorderColorByFeedbackType,
      getPendingData,
      store,
      collapseStates,
      toggleCollapse,
      isLoading,
      dateFormatter,
      refreshPendingPhases,
      isRefreshing,
      selectedCategory,
      setCategory,
      listCategories,
      handleAssignToMe,
      showModal,
      modalMessage,
      modalId,
      onModalClose,
      onModalAssignSingle,
      otherWorksList,
      validOtherWorksList,
      allWorksList,
      getTestWorkQueueTypeIcon,
      selectedPhases,
      togglePhaseSelection,
      isPhaseSelected,
      handleMultiAssign,
      assignParams,
      getMaterial,
      getElementsCount,
      selectedTypes,
      appliedSelectedTypes,
      testWorkQueueTypesOptions,
      onTypeFilterChange,
    };
  },
};
</script>

<template>
  <div class="card rounded-custom shadow border-0">
    <div class="card-header text-center rounded-only-top p-3">
      <div class="d-flex justify-content-center align-items-center mb-2">
        <strong class="text-uppercase me-3">pending</strong>
      </div>
      <div class="d-flex justify-content-center align-items-center mb-2 flex-wrap">
        <label class="form-label me-2 mb-0 text-violet fw-bold">Filtra per tipologia:</label>
        <Multiselect
          @change="onTypeFilterChange"
          @remove="onTypeFilterChange"
          @clear="onTypeFilterChange"
          v-model="selectedTypes"
          :options="testWorkQueueTypesOptions"
          mode="tags"
          :close-on-select="false"
          :clear-on-select="false"
          :max="3"
          placeholder="Seleziona tipologie..."
          class="me-3 multiselect-violet rounded-custom"
          style="min-width: 250px;"
        />
      </div>
      <div class="d-flex justify-content-center align-items-center">
        <div class="btn-group">
          <button
            class="btn"
            :class="{'btn-violet': selectedCategory === null, 'btn-outline-violet': selectedCategory !== null}"
            @click="setCategory(null)"
            :disabled="isRefreshing"
          >
            Tutte
          </button>
          <button
            class="btn"
            :class="{'btn-violet': selectedCategory === 'protesi-fissa', 'btn-outline-violet': selectedCategory !== listCategories.FISSA}"
            @click="setCategory(listCategories.FISSA)"
            :disabled="isRefreshing"
          >
            Fissa
          </button>
          <button
            class="btn"
            :class="{'btn-violet': selectedCategory === 'protesi-mobile', 'btn-outline-violet': selectedCategory !== listCategories.MOBILE}"
            @click="setCategory(listCategories.MOBILE)"
            :disabled="isRefreshing"
          >
            Mobile
          </button>
        </div>
      </div>
    </div>
    <div v-if="!isLoading" class="card-body">
      <ul class="list-group">
        <template v-for="testWorkQueuePhase in sortedAndLimitedPhases" :key="testWorkQueuePhase.test_work_queue.id">
          <li class="list-group-item d-flex justify-content-between align-items-start border-0 px-0">
            <div class="card shadow-sm w-100">
              <div class="card-header d-flex align-items-center" :class="getBorderColorByFeedbackType(testWorkQueuePhase)">
                <div class="me-2">
                  <input
                    type="checkbox"
                    :checked="isPhaseSelected(testWorkQueuePhase.id)"
                    @change="togglePhaseSelection(testWorkQueuePhase.id)"
                    @click.stop
                    class="form-check-input"
                  />
                </div>
                <div
                  class="flex-grow-1"
                  @click="toggleCollapse(testWorkQueuePhase.test_work_queue.id)"
                  style="cursor: pointer;"
                >
                <template v-if="testWorkQueuePhase.test_work_queue?.types?.length">
                  <img
                    v-for="type in testWorkQueuePhase.test_work_queue.types"
                    :key="type.slug"
                    :src="require(`@/assets/icons/${getTestWorkQueueTypeIcon(type.slug)}`)"
                    width="25"
                    :alt="`icon-${type.slug}-state`"
                    class="me-1"/>
                </template>
                <small>
                  {{ testWorkQueuePhase.test_work_queue.delivery_date ? dateFormatter(testWorkQueuePhase.test_work_queue.delivery_date) : '-' }} |
                  <strong>{{ testWorkQueuePhase.test_work_queue.prescription_test?.prescription?.prescription_rows?.[0]?.listRow?.name ?? '-' }}</strong> |
                  {{ testWorkQueuePhase.test_work_queue.prescription_test?.prescription?.customer?.first_name ?? '-' }}
                  {{ testWorkQueuePhase.test_work_queue.prescription_test?.prescription?.customer?.last_name ?? '-' }} |
                  {{ testWorkQueuePhase.test_work_queue.prescription_test?.name ?? '-' }}
                  <span v-html="testWorkQueuePhase.test_work_queue.prescription_test?.is_zebra ? '| <strong>Z</strong>' : ' '"></span>
                </small>
                </div>
              </div>
              <Transition>
                <div v-if="collapseStates[testWorkQueuePhase.test_work_queue.id]" class="p-3">
                  <p><strong>Codice prescrizione: </strong><small>{{ testWorkQueuePhase.test_work_queue.prescription_test?.prescription?.number_text ?? '-' }}</small></p>
                  <p><strong>Materiale: </strong><small>{{ getMaterial(testWorkQueuePhase) }}</small></p>
                  <p><strong>Elementi: </strong><small>{{ getElementsCount(testWorkQueuePhase) }}</small></p>
                  <p><strong>Note: </strong><small>{{ testWorkQueuePhase.test_work_queue.prescription_test?.notes ?? '-' }}</small></p>
                  <p><strong>Data pending: </strong><small>{{ getPendingData(testWorkQueuePhase) }}</small></p>

                </div>
              </Transition>
            </div>
          </li>
        </template>
      </ul>
      <StandardModal
        v-if="showModal"
        :modal_id="modalId"
        :title="$t('Attention')"
        :decline_label="$t('close')"
      >
        <template #default>
          <div class="mb-3 mt-3">{{ modalMessage }}</div>
          <div v-if="assignParams?.conflicts" class="mb-3">
            <div v-for="(conflict, index) in assignParams.conflicts" :key="index" class="mb-3">
              <h6 class="text-primary">
                <strong>Paziente: {{ conflict.customer.first_name }} {{ conflict.customer.last_name }}</strong>
              </h6>

              <div class="mb-2">
                <small class="text-muted">Lavori che vuoi assegnare:</small>
                <ul class="list-group list-group-flush">
                  <li v-for="phase in conflict.phases" :key="phase.id" class="list-group-item py-1 px-2">
                    <small>
                      <strong>{{ phase.test_work_queue.prescription_test?.name || '-' }}</strong> |
                      {{ phase.test_work_queue.prescription_test?.prescription?.number_text || '-' }}
                    </small>
                  </li>
                </ul>
              </div>

              <div class="mb-2">
                <small class="text-muted">Lavori già esistenti per questo paziente:</small>
                <ul class="list-group list-group-flush">
                  <li v-for="work in conflict.otherWorks" :key="work?.id" class="list-group-item py-1 px-2">
                    <small>
                      <strong>{{ work?.test_work_queue?.prescription_test?.name || '-' }}</strong> |
                      {{ work?.test_work_queue?.prescription_test?.prescription?.number_text || '-' }} |
                      <strong>Stato:</strong> {{ work?.state?.slug ?? '-' }} |
                      <strong>Assegnato a:</strong> {{ work?.user ? (work.user.first_name + ' ' + work.user.last_name) : 'Non assegnato' }}
                    </small>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <ul v-else class="list-group mb-3">
            <li v-for="work in allWorksList" :key="work?.id" class="list-group-item d-flex flex-column align-items-start">
              <div class="d-flex align-items-center mb-1">
                <label class="form-check-label">
                  <strong>Prescrizione:</strong> {{
                    Array.isArray(work?.test_work_queue?.prescription_test?.prescription?.prescription_rows) && work?.test_work_queue?.prescription_test?.prescription?.prescription_rows[0]?.listRow?.name
                      ? work.test_work_queue.prescription_test.prescription.prescription_rows[0].listRow.name
                      : '-'
                  }}
                </label>
              </div>
              <div>
                <strong>Stato:</strong>
                {{ work?.state?.slug ?? '-' }}
              </div>
              <div>
                <strong>Assegnato a:</strong>
                {{ work?.user ? (work.user.first_name + ' ' + work.user.last_name) : 'Non assegnato' }}
              </div>
            </li>
          </ul>

          <div class="d-flex gap-2 justify-content-center">
            <button
              class="btn btn-violet"
              @click="onModalAssignSingle"
            >{{ $t('Continue') }}</button>
          </div>
        </template>
      </StandardModal>
    </div>
    <div v-else class="d-flex justify-content-center p-5">
      <Spinner />
    </div>
    <div class="card-footer bg-white border-0">
      <div class="d-flex justify-content-center align-items-center gap-2">
        <button
          class="btn btn-sm btn-violet"
          @click="handleMultiAssign"
          :disabled="isLoading || selectedPhases.size === 0"
        >
          Assegna selezionati ({{ selectedPhases.size }})
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/scss/abstracts/_variables.scss";

.card-body {
  max-height: 605px;
  overflow: auto;
}

.bg-color-info {
  background-color: $light-violet;
}

.bg-color-warning {
  background-color: $red;
}

.bg-color-info,
.bg-color-warning {
  color: $white;
}

.card-header {
  cursor: pointer;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.v-enter-to,
.v-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>

<style>
.multiselect-violet {
  --ms-tag-bg: #A58ABA !important;
  --ms-tag-color: #FFFFFF !important;
  --ms-ring-color: #F3E9FF !important;
  --ms-border-color: #D7DCE0 !important;
  --ms-border-color-active: #A58ABA !important;
  --ms-option-bg-selected: #A58ABA !important;
  --ms-option-color-selected: #FFFFFF !important;
}

.multiselect-violet .multiselect,
.multiselect-violet .multiselect-tag,
.multiselect-violet .multiselect-option,
.multiselect-violet .multiselect-dropdown {
  border-radius: 1rem !important;
}
</style>
